import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';

import { envConfig } from '#constants/envConfig';
import { appLinkPath } from '../utils/appLinkPath';

/**
 * Opens teamplay.gg links inside the native shell instead of the browser.
 *
 * Android hands a verified App Link to the app as an `appUrlOpen` event (see the autoVerify
 * intent-filter in AndroidManifest.xml). We navigate to the link's PATH only — the app is
 * served from https://localhost, so loading the absolute URL would send the person back out to
 * the website.
 *
 * Two delivery paths, and the second one is the whole reason this hook is not three lines:
 *
 *  1. App already running → `appUrlOpen` fires and the listener below catches it.
 *  2. App launched by the link (cold start) → the intent is delivered before React mounts, so
 *     the event is already gone by the time we subscribe. `App.getLaunchUrl()` reads it back.
 *     Without it, tapping a link with the app closed lands on the dashboard, which reads as
 *     "the link is broken".
 *
 * Navigating only when the path differs from where we already are keeps the two paths from
 * doubling up on each other. No-op / tree-shaken on web. Mount once, inside the router provider.
 */
export const useNativeAppLinks = () => {
  const router = useRouter();

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let remove: (() => void) | undefined;
    let active = true;

    const open = (url: string | null | undefined) => {
      const path = appLinkPath(url);
      if (!path || path === router.state.location.href) {
        return;
      }
      router.history.push(path);
    };

    (async () => {
      const { App } = await import('@capacitor/app');

      // Cold start first, so a link that launched the app is honoured even if the listener
      // below never fires for it. Returns the OAuth/push deep link too when the app was started
      // that way — appLinkPath drops those, they have their own handlers.
      try {
        const launch = await App.getLaunchUrl();
        if (active) {
          open(launch?.url);
        }
      } catch {
        // No launch URL on a plain icon tap — nothing to do.
      }

      const handle = await App.addListener('appUrlOpen', ({ url }) => open(url));

      if (active) {
        remove = () => void handle.remove();
      } else {
        void handle.remove();
      }
    })();

    return () => {
      active = false;
      remove?.();
    };
  }, [router]);
};
